<template>
	<div class="header flex w-full items-center">
		<h3 class="flex-auto">{{ localization.t("administration.organizations.title") }}</h3>
		<button class="btn flex-none" @click="handleOpenCreate">
			{{ localization.t("administration.organizations.create") }}
		</button>
	</div>
	<div class="organizations flex w-full">
		<div class="organization-list flex-1" :class="selectedOrganizationId && 'condensed'">
			<organizations-list :items="organizations" :selected-organization-id="selectedOrganizationId"
				@on-details-click="handleDetailsOpen" @on-edit-click="handleOpenEdit" />
		</div>
		<div class="organization-details flex-1" v-if="selectedOrganizationId">
			<div class="flex rounded-t-lg border bg-gray-100 text-gray-500 align-middle justify-between px-3 py-3">
				<label class="flex-1">{{ localization.t("administration.organizations.details-title") }}</label>
				<XIcon class="h-8 w-8 cursor-pointer" @click="handleDetailsClose" />
			</div>
			<router-view :reload-key="detailsReloadKey" />
		</div>
	</div>

	<create-organization-dialog v-if="dialogOpen" :loading="loading.save" :edit-mode="!!editOrganization"
		:organization-data="editOrganization" @onSubmit="handleSubmit" @onCancel="handleCloseDialog" />
</template>

<script setup lang="ts">
import { onBeforeMount, Ref, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { XIcon } from "@heroicons/vue/outline";
import router from "@/router";
import OrganizationsList from "@/components/administration/organizations/list/list.vue";
import CreateOrganizationDialog from "@/components/administration/organizations/create-organization-dialog.vue";
import IOrganization, {
	ICreateOrganizationData,
	IUpdateOrganizationData,
} from "@/interfaces/organizations/iOrganization";
import { createOrganization, getOrganizations, updateOrganization } from "@/services/organizationsApi";
import { useExecuteAsync } from "@/composables/useExecuteAsync";

type LoadingFields = "list" | "save";

const route = useRoute();
const localization = useI18n();
const { loading, executeAsync } = useExecuteAsync();

const organizations: Ref<IOrganization[]> = ref([]);
const selectedOrganizationId: Ref<string | undefined> = ref((route.params.id as string) || undefined);
const dialogOpen = ref(false);
const editOrganization: Ref<IOrganization | undefined> = ref(undefined);
const detailsReloadKey = ref(0);

const fetchOrganizations = async () => {
	try {
		organizations.value = (await executeAsync<IOrganization[], LoadingFields>("list", getOrganizations)) ?? [];
	} catch {
		// executeAsync already showed the error toast; keep the current list
	}
};

onBeforeMount(fetchOrganizations);

// the route is the single source of the selection: covers X, the Organizations tab and browser Back
watch(
	() => route.params.id,
	(id) => {
		if (route.name !== "Organizations" && route.name !== "Organization") {
			return;
		}

		selectedOrganizationId.value = (id as string) || undefined;
	}
);

const handleDetailsOpen = (id: string) => router.push({ name: "Organization", params: { id } });

const handleDetailsClose = () => router.push({ name: "Organizations" });

const handleOpenCreate = () => {
	editOrganization.value = undefined;
	dialogOpen.value = true;
};

const handleOpenEdit = (id: string) => {
	editOrganization.value = organizations.value.find((o) => o.id === id);
	dialogOpen.value = true;
};

const handleCloseDialog = () => {
	dialogOpen.value = false;
	editOrganization.value = undefined;
};

const handleSubmit = async (data: ICreateOrganizationData | IUpdateOrganizationData) => {
	const isEdit = !!editOrganization.value;

	try {
		await executeAsync<void, LoadingFields>("save", () =>
			isEdit ? updateOrganization(data as IUpdateOrganizationData) : createOrganization(data)
		);
	} catch {
		// executeAsync already showed the error toast; keep the dialog open
		return;
	}

	handleCloseDialog();
	await fetchOrganizations();

	if (isEdit) {
		detailsReloadKey.value++;
	}
};
</script>

<style lang="postcss" scoped>
.header {
	height: 60px;
}

.organizations {
	height: calc(100% - 60px);
	@apply gap-x-4;
}

.organization-list.condensed {
	flex: 0 0 360px;
}

.organization-details {
	overflow-y: auto;
}
</style>
